'use client';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface OrderFormData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  imei?: string;
  caseNumber?: string;
  unlockingCode1?: string;
  unlockingCode2?: string;
  spcCode?: string;
  [key: string]: unknown;
}

interface OrderSummaryContextType {
  orderData: OrderFormData | null;
  setOrderData: (data: OrderFormData) => void;
  clearOrderData: () => void;
}

const OrderSummaryContext = createContext<OrderSummaryContextType | undefined>(undefined);

export function OrderSummaryProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [orderData, setOrderData] = useState<OrderFormData | null>(null);

  const updateOrderData = useCallback((data: OrderFormData) => {
    setOrderData(data);
  }, []);

  const clearOrderData = useCallback(() => {
    setOrderData(null);
  }, []);

  const value = useMemo(
    () => ({ orderData, setOrderData: updateOrderData, clearOrderData }),
    [orderData, updateOrderData, clearOrderData]
  );

  return (
    <OrderSummaryContext.Provider value={value}>
      {children}
    </OrderSummaryContext.Provider>
  );
}

export function useOrderSummary() {
  const context = useContext(OrderSummaryContext);
  if (context === undefined) {
    throw new Error('useOrderSummary must be used within an OrderSummaryProvider');
  }
  return context;
}
